import { useMemo, type CSSProperties } from "react";

import { cn } from "@/lib/utils";
import { CarrierLogo } from "@/components/common/CarrierLogo";
import { toLocalIsoDate } from "@/lib/dateHorizon";
import type { ScheduleEvent } from "@/types/schedule";
import { EVENT_DOT_COLORS } from "./scheduleConstants";

const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MAX_CHIPS = 3;

interface ScheduleMonthGridProps {
  /** Any date within the month to render. */
  anchor: Date;
  /** Flat, chronologically-sorted events spanning (at least) the month. */
  events: ScheduleEvent[];
  selectedId?: string | null;
  onSelect?: (event: ScheduleEvent) => void;
  /** Drill into a specific day (switches the page to day view). */
  onSelectDay?: (isoDate: string) => void;
  /** Phone layout: no 720px min-width, dots instead of title chips. */
  compact?: boolean;
}

interface Cell {
  date: Date;
  iso: string;
  inMonth: boolean;
  isToday: boolean;
  events: ScheduleEvent[];
  /** Stagger offset for the first visible chip on this day. */
  popStart: number;
}

/**
 * Month calendar (6 weeks × 7 days) with per-day milestone chips. Days outside
 * the anchor month are dimmed; clicking a day drills into the day view and
 * clicking a chip selects that event.
 */
export function ScheduleMonthGrid({
  anchor,
  events,
  selectedId,
  onSelect,
  onSelectDay,
  compact = false,
}: ScheduleMonthGridProps) {
  const cells = useMemo<Cell[]>(() => {
    const year = anchor.getFullYear();
    const month = anchor.getMonth();
    const first = new Date(year, month, 1);
    const dow = first.getDay(); // 0 = Sun .. 6 = Sat
    const mondayOffset = dow === 0 ? -6 : 1 - dow;
    const gridStart = new Date(first);
    gridStart.setDate(first.getDate() + mondayOffset);

    const byDate = new Map<string, ScheduleEvent[]>();
    for (const ev of events) {
      if (!ev.dateIso) continue;
      const bucket = byDate.get(ev.dateIso);
      if (bucket) bucket.push(ev);
      else byDate.set(ev.dateIso, [ev]);
    }

    const todayIso = toLocalIsoDate(new Date());
    let popStart = 0;
    return Array.from({ length: 42 }, (_, i) => {
      const date = new Date(gridStart);
      date.setDate(gridStart.getDate() + i);
      const iso = toLocalIsoDate(date);
      const dayEvents = byDate.get(iso) ?? [];
      const cell: Cell = {
        date,
        iso,
        inMonth: date.getMonth() === month,
        isToday: iso === todayIso,
        events: dayEvents,
        popStart,
      };
      popStart += Math.min(dayEvents.length, MAX_CHIPS);
      return cell;
    });
  }, [anchor, events]);

  return (
    <div className={compact ? "min-w-0" : "min-w-[720px]"}>
      <div className="grid grid-cols-7 border-b border-border/60 pb-2">
        {WEEKDAY_LABELS.map((label) => (
          <div
            key={label}
            className="px-1 text-center text-[10px] uppercase text-muted-foreground"
          >
            {compact ? label.slice(0, 1) : label}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-px">
        {cells.map((cell) => (
          <div
            key={cell.iso}
            className={cn(
              "flex flex-col gap-1 p-1.5",
              compact ? "min-h-11 items-center" : "min-h-[6.5rem]",
              !cell.inMonth && "opacity-40",
            )}
          >
            <button
              type="button"
              onClick={() => onSelectDay?.(cell.iso)}
              className={cn(
                "rounded-full text-xs tabular-nums transition-colors hover:bg-muted/60",
                compact ? "flex size-9 items-center justify-center" : "self-start px-1.5",
                cell.isToday
                  ? "bg-primary/15 font-semibold text-primary"
                  : "text-muted-foreground",
              )}
            >
              {cell.date.getDate()}
            </button>

            {compact ? (
              <button
                type="button"
                onClick={() => onSelectDay?.(cell.iso)}
                className="flex min-h-4 flex-wrap items-center justify-center gap-0.5"
                aria-label={
                  cell.events.length === 0
                    ? "No events"
                    : `${cell.events.length} events`
                }
              >
                {cell.events.slice(0, MAX_CHIPS).map((ev) => (
                  <span
                    key={ev.id}
                    className={cn(
                      "size-1.5 rounded-full",
                      EVENT_DOT_COLORS[ev.kind],
                    )}
                  />
                ))}
              </button>
            ) : (
              <div className="flex min-w-0 flex-col gap-0.5">
                {cell.events.slice(0, MAX_CHIPS).map((ev, chipIndex) => (
                  <button
                    key={ev.id}
                    type="button"
                    onClick={() => onSelect?.(ev)}
                    className={cn(
                      "schedule-event-pop flex min-w-0 items-center gap-1 rounded px-1 py-0.5 text-left transition-colors hover:bg-muted/50",
                      selectedId === ev.id && "bg-muted/70 ring-1 ring-primary/40",
                    )}
                    style={
                      {
                        "--schedule-pop-index": cell.popStart + chipIndex,
                      } as CSSProperties
                    }
                  >
                    <span
                      className={cn(
                        "size-1.5 shrink-0 rounded-full",
                        EVENT_DOT_COLORS[ev.kind],
                      )}
                    />
                    <CarrierLogo carrier={ev.carrier} logoOnly size={11} />
                    <span className="min-w-0 flex-1 truncate text-[11px] leading-tight">
                      {ev.title}
                    </span>
                    {ev.count && ev.count > 1 && (
                      <span className="shrink-0 text-[10px] font-semibold tabular-nums text-muted-foreground">
                        {ev.count}
                      </span>
                    )}
                  </button>
                ))}
                {cell.events.length > MAX_CHIPS && (
                  <button
                    type="button"
                    onClick={() => onSelectDay?.(cell.iso)}
                    className="px-1 text-left text-[10px] text-muted-foreground hover:text-foreground"
                  >
                    +{cell.events.length - MAX_CHIPS} more
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
